import type { Bundle } from '../validate';
import { rescueWavesDefaults } from './defaults';
import type { RescueFreeDef, RescueScenarioDef } from './types';

/*
 * The rescue wave clock (rescue.html §2 "The wave clock", §5 Admin "a wave clock preview … and the computed kill bound"): a port of
 * shared-cs `Raids/RescueRules` (BaseFreeSeconds, WaveSchedule, EscortWavesFor, MaxScheduledSpawns), S2-CR, so the preview and the
 * server's kill bound agree.
 */

/** `ScenarioLimits.MaxWaves`. */
const MAX_WAVES = 8;
/** `RescueRules.MaxEscortWaves`. */
export const MAX_ESCORT_WAVES = 8;

export type RescueWave = {
  phase: 'free' | 'escort';
  /** 1-based within its phase. */
  number: number;
  /** Seconds after the phase starts. */
  atS: number;
  size: number;
  /** The final free wave's extra zombie (a brute), or "". */
  extra: string;
};

const f32 = Math.fround;

/** `RescueRules.SecondDoorSeconds`. */
export const secondDoorSeconds = (s: RescueScenarioDef | null | undefined) =>
  (s?.free as RescueFreeDef | undefined)?.secondSeconds ?? 0;

/** `RescueRules.BaseFreeSeconds`: the base, plus the second door's when both are freed; at least 0.5 s. */
export const baseFreeSeconds = (s: RescueScenarioDef, both: boolean) =>
  Math.max(
    0.5,
    f32((s.free?.seconds ?? 60) + (both && secondDoorSeconds(s) > 0 ? secondDoorSeconds(s) : 0)),
  );

/** `RescueRules.WaveSchedule`. */
export function waveSchedule(
  s: RescueScenarioDef,
  squadSize: number,
  both: boolean,
  escortWaves: number,
  liveCap = 25,
): RescueWave[] {
  const list: RescueWave[] = [];
  const w = { ...rescueWavesDefaults(), ...(s.waves ?? {}) };
  const extra = Math.max(0, w.perExtraMember) * Math.max(0, squadSize - 1);
  const cap = Math.max(1, liveCap);
  const free = baseFreeSeconds(s, both);
  const every = Math.max(1, w.everyS);
  let n = 0;
  for (let at = Math.max(0, w.firstAtS); at < free && n < MAX_WAVES; at = f32(at + every)) {
    n++;
    list.push({
      phase: 'free',
      number: n,
      atS: at,
      size: Math.min(cap, Math.max(0, w.baseSize + w.growth * (n - 1) + extra)),
      extra: '',
    });
  }
  if (list.length > 0 && w.finalExtraZombieId) list[list.length - 1]!.extra = w.finalExtraZombieId;
  if (w.escortEveryS > 0 && w.escortSize > 0)
    for (let k = 1; k <= Math.min(Math.max(0, escortWaves), MAX_ESCORT_WAVES); k++)
      list.push({
        phase: 'escort',
        number: k,
        atS: f32(w.escortEveryS * k),
        size: Math.min(cap, w.escortSize + extra),
        extra: '',
      });
  return list;
}

/** `RescueRules.EscortWavesFor`: one per escortEveryS of the raid, capped. */
export function escortWavesFor(s: RescueScenarioDef, durationS: number): number {
  const every = s.waves?.escortEveryS ?? 0;
  if (every <= 0) return 0;
  return Math.min(MAX_ESCORT_WAVES, Math.max(0, Math.floor(Math.max(0, durationS) / every)));
}

/** `RescueRules.MaxScheduledSpawns`: both doors, every escort wave the duration allows, the final extra, the scenario's wave actions. */
export function maxScheduledSpawns(
  s: RescueScenarioDef,
  b: Bundle,
  squadSize: number,
  durationS: number,
): number {
  const cap = Math.max(1, (b.raidRules?.horde?.maxLiveZombies ?? 25) + Math.max(0, squadSize - 1));
  let total = 0;
  for (const w of waveSchedule(s, squadSize, true, escortWavesFor(s, durationS), cap))
    total += w.size + (w.extra ? 1 : 0);
  const scenario = s.scenarioId
    ? (b.scenarios ?? []).find((x) => x?.id === s.scenarioId)
    : undefined;
  for (const t of scenario?.triggers ?? [])
    for (const a of t?.do ?? []) {
      if (a?.type !== 'wave') continue;
      const text = (a.value ?? '').trim();
      total += /^[+-]?\d+$/.test(text) ? Math.max(0, Math.min(Number(text), cap)) : 6;
    }
  return total;
}

export type WaveClock = {
  squadSize: number;
  freeS: number;
  waves: RescueWave[];
  /** `MaxScheduledSpawns` for the raid length: what the rescue adds to the squad kill bound. */
  killBoundAdds: number;
};

/** One squad size's row: the schedule with both doors and the escort waves a raid of `durationS` allows. */
export function waveClock(
  s: RescueScenarioDef,
  b: Bundle,
  squadSize: number,
  durationS: number,
): WaveClock {
  const cap = Math.max(1, (b.raidRules?.horde?.maxLiveZombies ?? 25) + Math.max(0, squadSize - 1));
  return {
    squadSize,
    freeS: baseFreeSeconds(s, true),
    waves: waveSchedule(s, squadSize, true, escortWavesFor(s, durationS), cap),
    killBoundAdds: maxScheduledSpawns(s, b, squadSize, durationS),
  };
}
